import { Fragment, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Badge, Box, Button, Flex, Heading, Link as RadixLink, Table, Text } from '@radix-ui/themes'
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { useTRPC, useTRPCClient, type TRPCOutputs } from '~/utils/trpc'
import { formatTimestamp } from '~/utils/date'
import { computeStage, isRetired } from '~/features/installations/service'
import { downloadCsv } from '~/features/installations/exports'
import { stageColor, stageLabel } from '~/features/installations/constants'
import { LoggerMakerBadge } from '~/features/installations/components/LoggerMakerBadge'

type Order = TRPCOutputs['ops']['orders']['list'][number]

const ORDER_COLUMN_COUNT = 7

const OrderInstallationsDetail = ({ installationIds }: { installationIds: number[] }) => {
  const trpc = useTRPC()
  // An order is a record of what was bought, so a retirement since does not remove a line from it.
  // It also keeps `missingCount` meaning deleted, which is what the caption below claims.
  const installationsQuery = useQuery(
    trpc.ops.stationInstallations.list.queryOptions({ installationIds, includeRetired: true })
  )
  const orgsQuery = useQuery(trpc.ops.orgs.list.queryOptions())

  if (installationsQuery.isLoading) {
    return (
      <Text size="1" color="gray">
        Loading…
      </Text>
    )
  }
  if (installationsQuery.error) {
    return (
      <Text size="1" color="red">
        {installationsQuery.error.message}
      </Text>
    )
  }

  const installations = installationsQuery.data ?? []
  const orgNameById = new Map((orgsQuery.data ?? []).map((o) => [o.id, o.name]))
  const missingCount = installationIds.length - installations.length

  return (
    <Flex direction="column" gap="2">
      <Table.Root size="1" variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Org</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Site</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Profile</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Stage</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {installations.map((installation) => {
            const stage = computeStage(installation)
            return (
              <Table.Row key={installation.installation_id}>
                <Table.Cell>
                  <Link
                    to="/installations/$installationId"
                    params={{ installationId: String(installation.installation_id) }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--accent-11)] hover:underline"
                  >
                    <Text size="1" className="font-mono">
                      #{installation.installation_id}
                    </Text>
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Text size="1">{orgNameById.get(installation.org_id) ?? '—'}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="1">{installation.site?.name ?? '—'}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="1">{installation.name ?? '—'}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="1">{installation.installation_profile ?? '—'}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Flex align="center" gap="2">
                    <Badge color={stageColor[stage]} variant="soft" size="1">
                      {stageLabel[stage]}
                    </Badge>
                    {isRetired(installation) && (
                      <Badge
                        color="gray"
                        variant="soft"
                        size="1"
                        title={installation.retirement_reason ?? undefined}
                      >
                        retired
                      </Badge>
                    )}
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
      {missingCount > 0 && (
        <Text size="1" color="gray">
          {missingCount} installation{missingCount === 1 ? '' : 's'} from this order no longer exist.
        </Text>
      )}
    </Flex>
  )
}

const DownloadCsvLink = ({ orderId }: { orderId: string }) => {
  const trpcClient = useTRPCClient()
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(false)

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDownloading(true)
    setError(false)
    try {
      const { filename, csv } = await trpcClient.ops.orders.getCsv.query({ id: orderId })
      downloadCsv(filename, csv)
    } catch {
      setError(true)
    } finally {
      setDownloading(false)
    }
  }

  if (error) {
    return (
      <Text size="1" color="red">
        Failed
      </Text>
    )
  }

  return (
    <RadixLink href="#" size="1" onClick={handleClick} aria-disabled={downloading}>
      {downloading ? 'Downloading…' : 'Download CSV'}
    </RadixLink>
  )
}

const OrdersTable = ({ orders }: { orders: Order[] }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allExpanded = orders.length > 0 && expanded.size === orders.length
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(orders.map((o) => o.id)))

  if (orders.length === 0) {
    return (
      <Box py="6">
        <Text color="gray">No orders have been placed yet.</Text>
      </Box>
    )
  }

  return (
    <Flex direction="column" gap="2">
    <Flex justify="start">
      <Button size="1" variant="soft" onClick={toggleAll}>
        {allExpanded ? 'Collapse all' : 'Expand all'}
      </Button>
    </Flex>
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell />
          <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Provider</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Recipient</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Installations</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Ordered by</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {orders.map((order) => {
          const isOpen = expanded.has(order.id)
          return (
            <Fragment key={order.id}>
              <Table.Row className="cursor-pointer hover:bg-[var(--gray-a2)]" onClick={() => toggle(order.id)}>
                <Table.Cell>{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</Table.Cell>
                <Table.Cell>
                  <Text size="2">{formatTimestamp(order.created_at)}</Text>
                </Table.Cell>
                <Table.Cell>
                  <LoggerMakerBadge loggerMaker={order.provider} size="1" />
                </Table.Cell>
                <Table.Cell>
                  <Text size="2">{order.recipient}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="2">
                    {order.installation_ids.length} installation{order.installation_ids.length === 1 ? '' : 's'}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text size="2" color="gray">
                    {order.placed_by_email ?? '—'}
                  </Text>
                </Table.Cell>
                <Table.Cell onClick={(e) => e.stopPropagation()}>
                  <DownloadCsvLink orderId={order.id} />
                </Table.Cell>
              </Table.Row>
              {isOpen && (
                <Table.Row>
                  <Table.Cell colSpan={ORDER_COLUMN_COUNT}>
                    <Box p="2">
                      <OrderInstallationsDetail installationIds={order.installation_ids} />
                    </Box>
                  </Table.Cell>
                </Table.Row>
              )}
            </Fragment>
          )
        })}
      </Table.Body>
    </Table.Root>
    </Flex>
  )
}

const OrderHistoryPage = () => {
  const trpc = useTRPC()
  const ordersQuery = useQuery(trpc.ops.orders.list.queryOptions())

  return (
    <Flex direction="column" gap="4">
      <Heading size="6">Order history</Heading>

      {ordersQuery.isLoading ? (
        <Text color="gray">Loading…</Text>
      ) : ordersQuery.error ? (
        <Text color="red">{ordersQuery.error.message}</Text>
      ) : (
        <OrdersTable orders={ordersQuery.data ?? []} />
      )}
    </Flex>
  )
}

export const Route = createFileRoute('/orders/')({
  component: OrderHistoryPage
})
